// Fatia S4: gera textos de anúncio com IA e deixa escolher um.
// Usado pelo assistente de criação (S5a). Cada geração usa 1 crédito; repetir o mesmo pedido vem do cache (grátis).
import { useEffect, useRef, useState } from 'react'
import { AlertTriangle, Check, RefreshCw, Wand2 } from 'lucide-react'
import type { Goal } from '@impulso/shared'
import type { AdCopyVariantDto } from '@impulso/shared/api/ai'
import { errorMessage } from '../../lib/api'
import { creditsText, isQuotaError, useGenerateAdCopy } from './api'

export interface AdCopyGeneratorProps {
  companyId: string
  brief: string
  platform: 'meta' | 'google_ads'
  format: 'feed' | 'stories' | 'search'
  goal: Goal
  onPick: (v: { headline: string; body: string }) => void
}

const LIMITS = { google_ads: { headline: 30, body: 90 }, meta: { headline: 40, body: 125 } }
const len = (s: string) => [...s].length

export default function AdCopyGenerator({ companyId, brief, platform, format, goal, onPick }: AdCopyGeneratorProps) {
  const gen = useGenerateAdCopy(companyId)
  const [variants, setVariants] = useState<AdCopyVariantDto[] | null>(null)
  const [picked, setPicked] = useState<number | null>(null)
  const [creditsLeft, setCreditsLeft] = useState<number | null>(null)
  const [cached, setCached] = useState(false)
  const seen = useRef<string[]>([])
  const lastKey = useRef('')
  const key = `${brief.trim()}|${platform}|${format}|${goal}`
  const limits = LIMITS[platform]

  // Mudou o pedido (briefing/plataforma): as opções antigas deixam de valer para "gerar outras"
  useEffect(() => {
    if (lastKey.current && lastKey.current !== key) seen.current = []
  }, [key])

  const run = (fresh: boolean) => {
    if (brief.trim().length < 3) return
    lastKey.current = key
    gen.mutate(
      { brief: brief.trim(), platform, format, goal, ...(fresh ? { fresh: true, avoid: seen.current.slice(-30) } : {}) },
      {
        onSuccess: (r) => {
          setVariants(r.variants)
          setPicked(null)
          setCreditsLeft(r.creditsLeft)
          setCached(r.cached)
          seen.current = [...seen.current, ...r.variants.flatMap((v) => [v.headline, v.body])]
        },
      },
    )
  }

  const pick = (i: number) => {
    const v = variants?.[i]
    if (!v) return
    setPicked(i)
    onPick({ headline: v.headline, body: v.body })
  }

  const stale = variants && lastKey.current !== key
  const credits = creditsText(creditsLeft)

  return (
    <div className="stack" style={{ gap: 12 }}>
      <div className="row wrap" style={{ gap: 10 }}>
        <button type="button" className="btn primary" onClick={() => run(!!variants && !stale)} disabled={gen.isPending || brief.trim().length < 3}>
          {gen.isPending ? <RefreshCw size={16} className="spin" /> : <Wand2 size={16} />}
          {gen.isPending ? 'Escrevendo…' : variants && !stale ? 'Gerar outras opções' : 'Gerar textos com IA'}
        </button>
        {credits && <span className="small muted">{credits}{cached ? ' · mesmas opções de antes, sem gastar crédito' : ''}</span>}
      </div>

      {gen.error && (
        <div className={isQuotaError(gen.error) ? 'callout amber' : 'form-error'} role="alert">
          {isQuotaError(gen.error) && <AlertTriangle size={18} style={{ flexShrink: 0 }} />}
          <span>{errorMessage(gen.error)}</span>
        </div>
      )}

      {variants && variants.length > 0 && (
        <div className="stack" style={{ gap: 10 }} aria-live="polite">
          <label className="small strong muted">ESCOLHA UM TEXTO (você pode editar depois){stale ? ' · o pedido mudou: gere de novo para atualizar' : ''}</label>
          {variants.map((v, i) => (
            <button key={`${v.headline}-${i}`} type="button" className={`option-card ${picked === i ? 'on' : ''}`} onClick={() => pick(i)} style={{ padding: 14 }} aria-pressed={picked === i}>
              <span style={{ minWidth: 0 }}>
                <span className="row wrap" style={{ gap: 6, marginBottom: 4 }}>
                  <span className="badge purple">{v.angle}</span>
                  <span className="small muted">{len(v.headline)}/{limits.headline} · {len(v.body)}/{limits.body} caracteres</span>
                </span>
                <span className="strong" style={{ display: 'block', overflowWrap: 'anywhere' }}>{v.headline}</span>
                <span className="small" style={{ display: 'block', color: 'var(--text-2)', overflowWrap: 'anywhere' }}>{v.body}</span>
                <span className="small muted" style={{ display: 'block', marginTop: 4 }}>Botão: {v.cta} · {v.rationale}</span>
              </span>
              {picked === i ? <Check size={18} color="var(--primary)" style={{ flexShrink: 0 }} /> : <span className="radio" />}
            </button>
          ))}
        </div>
      )}
      {variants && variants.length === 0 && <p className="small muted">Não saiu nenhuma opção desta vez. Tente gerar de novo.</p>}
    </div>
  )
}
